/**
 * ProductMetadataEditor.tsx — product_metadata 业务效果录入/编辑弹窗（ADM-PAY-S006）
 *
 * 运营在 Adapty 加 product 后，按 (vendor_product_id, payment_method) 在此录入「业务效果」（识别名/coin/bonus/
 * 有效期/标题）。per-通道（#306）：每 product 每通道（IAP / STRIPE）各一行。新建+编辑合一（isEdit=!!item?.id）。
 *
 * **#465 Stripe 权益受控派生**：`bonus_ratio` 改**响应派生**（不再手输，只读展示）；STRIPE 行权益由服务端按
 * IAP 基线 + App `third_party_bonus_ratio` 派生：
 *   - coin **锁定基线**（不录入、提交省略 → 服务端复制 IAP coin）；
 *   - bonus（CONSUMABLE 发币与周期发币订阅同一规则，70 §2）只可 **≥ 派生下限**：新建留空 = 服务端预填派生下限；
 *     编辑态显示当前下限，留空 = 保持已存值（PATCH 不带 bonus 即不改），「套用派生下限」一键填入当前下限；
 *   - 订阅周期**仅 WEEKLY / MONTHLY**（须与 IAP 基线同周期）；access_level_id **必填**（据此唯一命中 IAP 基线）。
 * IAP 行不受门禁（基线自身），coin/bonus 照常录入。
 *
 * **订阅效果（70 §2 `subscription_effect`）**：原生目录只售周期发币 `COIN_PERIOD_GRANT` —— 每计费周期（首购 / 续订 /
 * RESUBSCRIBE）一次性发放该周期 COIN + BONUS；新建订阅显式提交该值，编辑态只读展示（创建后不可换）。「补通道」
 * 沿用同逻辑产品既有行的效果（同 access level 效果须一致），故给存量 `VIP_READING` 产品补通道仍提交 `VIP_READING`。
 * `VIP_READING` 行不发币，COIN / BONUS 锁 0。每日赠送（`daily_grant_amount`）已冻结，不再录入、不再展示。
 *
 * **框架通道行（PayerMax，设计稿 B2）**：权益派生与 STRIPE 同口径（coin 锁基线、bonus ≥ 派生下限、access_level_id 必填，
 * 70 §2）；`product_type` 锁 CONSUMABLE（框架通道只售一次性商品，A8 §2 校验③）；**不渲染、不提交** `stripe_price_id`；
 * vendor_product_id 是 Adapty 自定义 store 的 product ID，不套 `prod_` 告警。原生成交价不在本弹窗，经列表「设置价格」
 * （A8 §10.3）单独配置。
 *
 * product_type 联动：CONSUMABLE 无 subscription_period / subscription_effect。
 * 金额是非负整数串（minor 串，上界 int8）→ input 存 string + 过滤非数字，不经 Number。
 * 成功 invalidate ['product-metadata', appId] + 关闭；失败 toast 不关弹窗。RBAC：写动作 root/admin/operator（后端权威）。
 *
 * **#400 权益效果编辑（dd614）**：CONSUMABLE 行录 `consumable_effect` + 逐效果参数（契约 `03-数据字典/70-产品配置.md`
 * §2.5 / `02-API契约/A8-运营支付.md` §2·§3）。UI 把服务端的四道拒绝面**前置**成不可填，而不是让运营填完吃 422/409：
 *   - `consumable_effect` **创建后不可改**（A8 §3 `.strict()` 拒传）→ 编辑态选择器置灰（disabled），仍显示当前值；
 *   - `READING_PASS_CARD` 三列权益金额与 `bonus_expire_days` **恒 0**（§2.5 谓词）→ 选中即锁成只读 `0`，不渲染 input；
 *   - 创建阅读卡**不展示 / 不提交** `tier_id`（IAP 服务端 UUID v7，STRIPE 继承 IAP 基线）；
 *   - 编辑态 `tier_id` 只读文本；配对任一行 `first_published_at` 非空即锁时长；
 *   - `access_level_id` 是产品级关联、**创建后固定**（A8 §3：PATCH 传入即 `VALIDATION_FAILED`）→ 编辑态只读文本、不提交。
 *
 * **#619 ③ BONUS 有效期恒永久**：`bonus_expire_days` 提交恒 `0`（= 永久不过期），后台不提供编辑入口 ——
 * 与上面 `READING_PASS_CARD` 的「效果谓词锁 0」不同，本条是**跨全部效果**的运营口径收窄（发币行也不再可配
 * 限期赠送）。契约侧 `03-数据字典/70-产品配置.md` §2 仍留「运营按需设非 0」的旧描述，已起 dev-docs 订正 PR。
 * 提交侧：`consumable_effect_params` 是**逻辑产品级原子更新**（A8 §3）—— 只 PATCH 当前这一行，服务端在同一事务
 * 锁 IAP / STRIPE 配对行同写；**不由 UI 逐行 PATCH 两次**（两次请求之间会留下配对失配态，而配对一致是 is_serveable 前置）。
 */

import {
  CONSUMABLE_EFFECTS,
  type ConsumableEffect,
  type CreateProductMetadataRequest,
  MAX_READING_PASS_DURATION_SECONDS,
  type ProductType,
  SETTLEMENT_CHANNELS,
  STRIPE_SUBSCRIPTION_PERIODS,
  type SettlementChannel,
  type SubscriptionEffect,
  type SubscriptionPeriod,
  type TitleI18n,
  type UpdateProductMetadataRequest,
  isFrameworkSettlementChannel,
  isIapPairedSettlementChannel,
  isStripeSubscriptionPeriod,
  isZeroEntitlementEffect,
} from '@novel-admin/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  consumableEffectLabel,
  formatDurationSeconds,
  paymentMethodLabel,
  readingPassDurationSecondsOf,
  readingPassTierIdOf,
  subscriptionEffectLabel,
} from '~/data/adapters/product-metadata-adapter';
import { channelShortLabel, channelStoreLabel } from '~/data/adapters/product-metadata-grouping';
import { describeProductMetadataSaveError } from '~/data/adapters/product-metadata-save-error';
import { type ProductMetadataRow, useAdaptyCatalog, useSysConfig } from '~/data/queries';
import { productMetadataApi } from '~/hooks/use-product-metadata';
import { Icons } from '~/lib/icons';
import { useAuthStore } from '~/stores/auth-store';
import { Dialog } from '~/ui/Dialog';
import { type FormRowProps, FormRow as UIFormRow } from '~/ui/FormRow';
import { MultiLangFormRows, type MultiLangValue } from '~/ui/MultiLangFormRows';
import { Select } from '~/ui/Select';
import { toast } from '~/ui/Toast';

const FORBIDDEN_ROLE_TITLE = '权限不足 · 该操作受角色限制（后端 requireRole 为权威）';

/**
 * STRIPE 行 vendor_product_id 的 canonical 格式（A8 §9.6）—— Stripe Product id `prod_…`。
 *
 * 与 `stripe_price_id` 的 `^price_…$` 是**同一道 `is_checkout_routable` 判据的两半**：C 端
 * 要求 STRIPE 候选同时满足 `vendor_product_id ~ ^prod_…$` 与 `stripe_price_id ~ ^price_…$`
 * 才放行 `POST /payment/sessions`，否则返 `PAYMENT_STRIPE_CHECKOUT_ERROR`
 * （`is_retryable=false`）。
 *
 * **只告警、不拦写入**：契约把两半刻意做成非对称的 ——
 * `stripe_price_id` 在 A8 §9.6 是**拒收句**（「STRIPE 非 null 时须匹配」→ shared Zod + DB CHECK 落实），
 * 而 `vendor_product_id` 只有**后果句**（「只有匹配……才能通过 direct Checkout routability」）。
 * `02-API契约/60-支付与订阅.md` §1「候选渠道集」/ §2.2「配置类失败的可发现性」与
 * `03-数据字典/70-产品配置.md` §3「STRIPE 另需 provider readiness」三处一致规定：
 * canonical 缺失或错配按 SRV-PAY-029「配置错配按运营配置事故处理」承接，**不在写入侧拦截、
 * 也不在 serving 侧隐藏商品**，可发现性是契约要求（而非实施可选项）。
 * 故本判据只驱动**可见告警**：录入编辑器的候选源是 C 端真实成交记录，一条已成交但 vendor id 非
 * canonical 的商品必须仍能建行（否则 webhook 按 `(app_id, vendor_product_id, store→通道)` 反查
 * 不命中 → 写 DLQ、用户已扣款却拿不到 coin/bonus，反而堵死补救真实事故的路径）。
 *
 * **不做 `trim()`**：C 端判据直接对库值取正则、不做任何空白归一。此处若 trim，带首尾空白的库值
 * （直连 DB / 回填脚本落进来的）会被 admin 判成合规而不挂徽章、C 端却判不 routable ——
 * 正是本项要消灭的「下发正常、点了必炸、admin 无告警」。输入框侧的空白过滤在 onChange 里做。
 *
 * 仅对 STRIPE 行成立：IAP 行的 vendor_product_id 是 Apple / Google 商店 SKU（形如
 * `com.rivmoon.app.coins_500`），与本格式无关，不得误伤。
 *
 * @cross-repo novel-app-server:src/services/product-metadata-serveability.ts:598
 *   （`isProductMetadataCheckoutRoutable`。`^prod_…$` 全域共 **2 份**：C 端那条 + 本常量
 *   —— `git grep '\^prod_\[A-Za-z0-9\]'` 两仓合计只此两处。此前写「第三份副本」是把另一条
 *   `^price_…$` 的副本数算到了 `prod_` 头上（`price_` 在 admin 有 4 处、C 端 2 处，正是下沉时
 *   必须连它一起搬的原因，见 docs/plans NOT-in-scope §2）。
 *   本文件在 `packages/web`，`check-mirror-drift`（只扫 `schema/shared/*`）与
 *   `check-content-origin-parity` 都够不着。改本常量前先核 C 端那条，两边必须逐字一致。）
 */
export const STRIPE_VENDOR_PRODUCT_ID_PATTERN = /^prod_[A-Za-z0-9]{1,249}$/;

/**
 * 格式判据的**完整**文字表述：三条约束缺一不可（前缀 · 字符集 · 长度）。
 * 只说「须 `prod_` 开头」会让 `prod_coins_500`（Stripe 允许自定义含下划线的 Product id）
 * 与 `prod_`（后缀 0 字符）这类值看起来合规，运营无从自查。
 */
export const STRIPE_VENDOR_PRODUCT_ID_FORMAT_HINT =
  '格式须为 prod_ + 1~249 位字母或数字（后缀只允许 A-Z / a-z / 0-9，不含 - _ . 等符号），如 prod_SxYz1234。';

/**
 * 后果的**准确**表述：**只陈述本项自身的后果，不预测这一行会不会被下发。**
 *
 * 下发不是两个条件的合取。`02-API契约/60-支付与订阅.md` §1 的候选式已是四项（策略成员 ∩
 * placement ∩ 该 App 的 active Stripe Checkout config ∩ `is_serveable`），其上还叠两条用户级门禁
 * （SRV-PAY-003：`third_party_payment_unlocked_at IS NULL` 时只返 IAP、隐藏候选内全部非 IAP 通道；
 * SRV-PAY-033：审核标签收窄）。而 `03-数据字典/70-产品配置.md` §3「非成员通道恒不可见」把通道启停
 * 的**唯一权威**判给成员关系：「即便本表已配 `adapty_placement_id`、provider 已就绪、商品可售，
 * 该通道不在生效策略的成员集合内即不进候选、不返 `payment_methods` 行」。
 *
 * 故「只要该行可售且该 App 已接入 direct Checkout，就会照常下发」是取六条里的两条当充分条件 ——
 * 契约原文正好否定它。这个徽章回答不了「会不会下发」，也不该回答；沿用本仓
 * `data/adapters/app-payment-methods-adapter.ts` 的口径：**宁可不说，也不说错**。
 *
 * 「服务端不拒绝保存」同样收成「不**因本项**而拒」：对本字段格式属实，但服务端仍可能因
 * access_level_id 必填 / 无 IAP 基线 / `RESOURCE_CONFLICT` 拒掉这次保存，绝对句同样是说满。
 */
export const STRIPE_VENDOR_PRODUCT_ID_IMPACT_HINT =
  '本项不改变该行的可售判定，服务端也不因本项拒绝保存；后果只落在这一项上：该行发不起 Stripe direct Checkout —— 它若被下发、用户点击支付，POST /payment/sessions 返回不可重试的错误。该行会不会被下发，由本项之外的条件决定（通道策略成员关系 · placement · provider readiness · 可售 · 用户级门禁），本提示不作判断。';

/**
 * 补救路径：vendor_product_id 是身份字段，只能删行重建 —— 而删除本身是 A8 §4 明列的高危动作
 * （webhook 首购 / RESUBSCRIBE 按三元反查 live `product_metadata` 不命中 → 写 DLQ + 不发放，用户已扣款），
 * 且 `operator` 无此权（root/admin 收窄）。一条非 canonical 的行可能**正在正常发币**（只是发不起
 * direct Checkout），删掉它反而制造更严重的事故，故必须把前置确认与次序写在提示里。
 *
 * ③ 的理由取 `product_metadata_app_access_level_method_unique`
 * （`packages/server/src/db/schema/shared/product-metadata.ts:191-195`，`(app_id, access_level_id,
 * payment_method)`，谓词 `access_level_id IS NOT NULL AND <> '' AND deleted_at IS NULL`），而**不是**
 * `product_metadata_app_stripe_price_unique`（同文件 `:201-205`）：后者谓词含
 * `stripe_price_id IS NOT NULL`，两半都不合规（price 为 null）时它根本不参与，理由会当场落空。
 * access_level 那条对**任何** STRIPE 行都成立（STRIPE 行 access_level_id 必填，见
 * `packages/shared/src/schemas/product-metadata.ts:482-490` 的 superRefine），与 price 有没有值无关。
 */
export const STRIPE_VENDOR_PRODUCT_ID_REBUILD_HINT =
  'vendor_product_id 是身份字段、创建后不可改，唯一出路是删除该行后按 prod_ 格式重建：① 删除前先确认该值没有在途履约或已成交订单（webhook 按 vendor_product_id 反查发放规则，删早了会漏发 coin/bonus）；② 删除是高危操作，仅 root/admin 可执行；③ 次序必须先删后建（同 App 同 access_level 的 active STRIPE 行唯一，而 STRIPE 行 access_level_id 必填，先建必撞 RESOURCE_CONFLICT）。';

/** STRIPE 行的 vendor_product_id 是否为 canonical `prod_…`（本弹窗与列表页徽章共用一份判据）*/
export function isCanonicalStripeVendorProductId(vendorProductId: string): boolean {
  return STRIPE_VENDOR_PRODUCT_ID_PATTERN.test(vendorProductId);
}

/** dialogs 惯例：FormRow 固定 labelWidth=140 / gap=14 / pad='12px 0' / labelPadTop=6 */
const FormRow = (p: FormRowProps) => (
  <UIFormRow labelWidth={140} gap={14} pad="12px 0" labelPadTop={6} {...p} />
);

const PRODUCT_TYPE_OPTIONS: { value: ProductType; label: string }[] = [
  { value: 'CONSUMABLE', label: '充值（CONSUMABLE）' },
  { value: 'SUBSCRIPTION', label: '订阅（SUBSCRIPTION）' },
];
/** 支付通道选项按结算通道闭集生成（含已登记框架通道），不在页面里另立一份通道清单 */
const PAYMENT_METHOD_OPTIONS: { value: SettlementChannel; label: string }[] =
  SETTLEMENT_CHANNELS.map((value) => ({
    value,
    label:
      value === 'IAP' ? '应用内购买（IAP）' : paymentMethodLabel(value).replace('三方', '三方支付'),
  }));
const PERIOD_OPTIONS: { value: SubscriptionPeriod; label: string }[] = [
  { value: 'WEEKLY', label: '周（WEEKLY）' },
  { value: 'MONTHLY', label: '月（MONTHLY）' },
  { value: 'QUARTERLY', label: '季（QUARTERLY）' },
  { value: 'YEARLY', label: '年（YEARLY）' },
];
/** consumable_effect 闭集选项（§2.5 单点定义；选项集直接由 shared 常量派生，不在 UI 另立一份闭集）*/
const EFFECT_OPTIONS: { value: ConsumableEffect; label: string }[] = CONSUMABLE_EFFECTS.map(
  (e) => ({
    value: e,
    label: `${consumableEffectLabel(e)}（${e}）`,
  }),
);
/** 时长常用档（秒）——只是输入辅助，值域权威在 §2.5：(0, int4 上限] 的整数，运营可填任意值 */
const DURATION_PRESETS: { seconds: number; label: string }[] = [
  { seconds: 3600, label: '1 小时' },
  { seconds: 7200, label: '2 小时' },
  { seconds: 10800, label: '3 小时' },
];

/** 受控非负整数串 input（coin/bonus/时长：存 string + 过滤非数字，避免 Number 丢 int8 精度） */
function DigitInput({
  value,
  onChange,
  width = 160,
  disabled,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  width?: number;
  disabled?: boolean;
  placeholder?: string;
}) {
  return (
    <input
      className="input mono"
      inputMode="numeric"
      style={{ width }}
      value={value}
      disabled={disabled}
      placeholder={placeholder}
      // 过滤非数字 + 去前导零（保留单个 '0'）：默认 '0' 后接键入会得 '0500' 前导零串，触发后端 ^(0|[1-9][0-9]*)$ 拒 → 422
      onChange={(e) => onChange(e.target.value.replace(/[^0-9]/g, '').replace(/^0+(?=\d)/, ''))}
    />
  );
}

/** 派生 bonus_ratio 展示（#465 只读；decimal string → 「+X%」）*/
function formatBonusRatio(ratio: string): string {
  const n = Number(ratio);
  return n > 0 ? `+${Math.round(n * 100)}%` : '—';
}

export function ProductMetadataEditor({
  appId,
  item,
  prefillTitle,
  prefillAccessLevel,
  prefillEffectFrom,
  pairedChannels,
  prefillPaymentMethod,
  existingChannels,
  prefillBonusFloor,
  platform,
  onClose,
}: {
  appId: string;
  item?: ProductMetadataRow;
  /** 新建时从 Adapty 镜像行带入的产品标题：seed title.en / name（运营可改）；编辑态忽略 */
  prefillTitle?: string;
  /** 新建通道时从所属逻辑产品带入的 access_level_id（「补通道」预填，运营可改）；编辑态用已存值 */
  prefillAccessLevel?: string;
  /**
   * 「补通道」时用来预填效果与效果参数的**同逻辑产品既有行**。§2.5 跨通道一致性要求两行 `consumable_effect`
   * 相同、`consumable_effect_params` 逐 key 逐值相同，不满足则 STRIPE 行 `is_serveable` 不成立 ——
   * 让运营照着另一行手抄 tier_id / duration_seconds 必然抄错，故直接带过来。编辑态忽略（用本行已存值）。
   */
  prefillEffectFrom?: ProductMetadataRow;
  /**
   * 该逻辑产品（同 access_level_id）的全部通道行，含本行。**改 duration_seconds 的生命周期 gate
   * （A8 §3 / #773）判据**：配对任一行 `first_published_at` 非空即锁死时长输入。
   */
  pairedChannels?: ProductMetadataRow[];
  /** 「补通道 · X」预选的通道；编辑态忽略 */
  prefillPaymentMethod?: SettlementChannel;
  /** 「补通道」时该逻辑产品已有行的通道：下拉里置灰（每 product 每通道一行）；编辑态忽略 */
  existingChannels?: SettlementChannel[];
  /**
   * 「补通道」时同逻辑产品既有配对行（STRIPE / 框架通道）的派生 bonus 下限 —— 配对行同一公式
   * `IAP bonus + ceil(IAP coin × ratio)`，新建前即可展示「BONUS（≥ N）」；无既有配对行时 undefined（服务端派生）
   */
  prefillBonusFloor?: string;
  /** App 平台（apps.platform）：把 IAP 派生成 App Store / Google Play 展示名，与列表页同口径 */
  platform?: string;
  onClose: () => void;
}) {
  const isEdit = !!item?.id;
  const queryClient = useQueryClient();
  const role = useAuthStore((s) => s.user?.role);
  const canWrite = role === 'root' || role === 'admin' || role === 'operator';

  // 「其他语言」全集 = useSysConfig 真数据 enabled 语言（MultiLangFormRows 默认 fallback 仅 zh/en）
  const { data: sys } = useSysConfig();
  const enabledLangs = sys.languages.filter((l) => l.enabled).map((l) => l.language_code);

  // 身份字段（编辑态只读）—— per-通道：vendor_product_id + payment_method 共同定位一行
  const [vendorProductId, setVendorProductId] = useState(item?.vendorProductId ?? '');
  const [paymentMethod, setPaymentMethod] = useState<SettlementChannel>(
    item?.paymentMethod ?? prefillPaymentMethod ?? 'IAP',
  );
  // 补通道沿用同逻辑产品的 product_type（product 级字段，跨通道须同值）；框架通道只售一次性商品
  const [productType, setProductType] = useState<ProductType>(
    item?.productType ??
      (prefillPaymentMethod && isFrameworkSettlementChannel(prefillPaymentMethod)
        ? 'CONSUMABLE'
        : (prefillEffectFrom?.productType ?? 'CONSUMABLE')),
  );
  // 业务效果字段。补通道时周期取同逻辑产品既有行（STRIPE 订阅周期须与 IAP 基线相同，否则 PRODUCT_FIELDS_MISMATCH）
  const [subscriptionPeriod, setSubscriptionPeriod] = useState<SubscriptionPeriod | ''>(
    item?.subscriptionPeriod ?? prefillEffectFrom?.subscriptionPeriod ?? '',
  );
  const [name, setName] = useState(item?.name ?? prefillTitle ?? '');
  const [accessLevelId, setAccessLevelId] = useState(
    item?.accessLevelId ?? prefillAccessLevel ?? '',
  );
  // STRIPE direct Checkout Price 路由（A8 §9.6，#352）：仅 STRIPE 行可填，IAP 行恒 null。
  // 不参与可售判定——留空只是该行不能发起 direct Checkout，不影响 Adapty 展示与付款后履约。
  const [stripePriceId, setStripePriceId] = useState(item?.stripePriceId ?? '');
  const [title, setTitle] = useState<MultiLangValue>({
    ...(item?.titleI18n ?? { en: prefillTitle ?? '' }),
  });
  const [coinAmount, setCoinAmount] = useState(item?.coinAmount ?? '0');
  const [bonusAmount, setBonusAmount] = useState(item?.bonusAmount ?? (isEdit ? '0' : ''));
  // 权益效果（#400）：新建可选、编辑态不可改（A8 §3 `.strict()` 拒传 consumable_effect）。
  // CONSUMABLE 新建缺省 COIN_GRANT —— 与服务端 resolveConsumableEffectDefault / migration 0113 回填口径同源。
  // 新建「补通道」时从同逻辑产品既有行带入（§2.5 跨通道一致性），否则缺省 COIN_GRANT。
  const effectSeed = item ?? prefillEffectFrom;
  const [consumableEffect, setConsumableEffect] = useState<ConsumableEffect>(
    effectSeed?.consumableEffect ?? 'COIN_GRANT',
  );
  // READING_PASS_CARD 的两个 params：tier_id（产品位身份，创建后不可改）+ duration_seconds（权益时长，可调）
  const [tierId] = useState((effectSeed && readingPassTierIdOf(effectSeed)) ?? '');
  const [durationSeconds, setDurationSeconds] = useState(
    effectSeed ? (readingPassDurationSecondsOf(effectSeed)?.toString() ?? '') : '',
  );
  // bonus_ratio 改响应派生（#465）：不再手输，仅只读展示 item.bonusRatio
  // is_active 不在 create/update 契约：启停经列表「上架/下架」按钮走 activate/deactivate（收窄 root/admin）

  const adaptyCatalog = useAdaptyCatalog(appId);
  const unconfiguredCandidates = (adaptyCatalog.data?.products ?? []).filter(
    (p) => !p.configured_methods.includes(paymentMethod),
  );

  const isSubscription = productType === 'SUBSCRIPTION';
  const isStripe = paymentMethod === 'STRIPE';
  // 以 IAP 行为基线派生权益的配对通道（STRIPE ∪ 框架通道，70 §2）：coin 锁基线、bonus ≥ 派生下限、access_level 必填
  const isIapPaired = isIapPairedSettlementChannel(paymentMethod);
  // 框架通道（PayerMax）：只售一次性商品、无 stripe_price_id、原生价格另经 A8 §10.3 配置
  const isFramework = isFrameworkSettlementChannel(paymentMethod);
  const channelName = channelShortLabel(paymentMethod, platform);
  const isAddChannel = !isEdit && !!prefillEffectFrom && !!prefillAccessLevel;
  const [saveError, setSaveError] = useState<ReturnType<
    typeof describeProductMetadataSaveError
  > | null>(null);
  // 订阅效果：编辑态取已存值（服务端已归一，创建后不可换）；补通道沿用同逻辑产品既有订阅行的效果（同 App 同
  // access level 已登记效果须一致，否则服务端 RESOURCE_CONFLICT）；全新录入恒周期发币（原生目录只售 COIN_PERIOD_GRANT）
  const seededSubscriptionEffect =
    effectSeed?.productType === 'SUBSCRIPTION' ? effectSeed.subscriptionEffect : null;
  const subscriptionEffect: SubscriptionEffect | null = isSubscription
    ? (seededSubscriptionEffect ?? 'COIN_PERIOD_GRANT')
    : null;
  // VIP_READING 不发币（70 §2：coin / bonus 恒 0），与不发币一次性效果同样锁金额
  const isVipReading = subscriptionEffect === 'VIP_READING';
  // STRIPE direct Checkout 路由 gate 的另一半（A8 §9.6）：vendor_product_id 非 canonical `prod_…` 时
  // 该行的 is_serveable 不变，但一旦可售且该 App 已接入 Checkout，用户点击支付必然失败且不可重试。
  // **只告警、不进 canSubmit**（契约明文不在写入侧拦截，见 STRIPE_VENDOR_PRODUCT_ID_PATTERN 注释）。
  // 空值另有「必填」判据管，这里不重复报错。
  const stripeVendorIdMalformed =
    isStripe && vendorProductId.trim() !== '' && !isCanonicalStripeVendorProductId(vendorProductId);
  // 原生 STRIPE 订阅（两种效果）与 VIP 畅读（任一通道，70 §2 effect_wellformed）仅 WEEKLY / MONTHLY：
  // 限制周期选项；QUARTERLY / YEARLY 只留给 IAP 周期发币
  const periodLimitedToWeeklyMonthly = isStripe || isVipReading;
  const periodOptions = periodLimitedToWeeklyMonthly
    ? PERIOD_OPTIONS.filter((o) =>
        (STRIPE_SUBSCRIPTION_PERIODS as readonly SubscriptionPeriod[]).includes(o.value),
      )
    : PERIOD_OPTIONS;
  // 编辑态配对行（STRIPE / 框架通道）的派生 bonus 下限（服务端派生，CONSUMABLE 发币与周期发币订阅同一公式；新建时未知 → 留空用服务端派生）
  const minBonus = item?.derivedMinimumBonusAmount ?? null;
  // 新建配对行的展示下限（只作提示；最终以服务端派生裁决）
  const createBonusFloor = !isEdit && isIapPaired ? (prefillBonusFloor ?? null) : null;

  // ---- #400 效果联动 ----
  // 效果只挂 CONSUMABLE（SUBSCRIPTION 传非 null 即 VALIDATION_FAILED，A8 §2）
  const effectiveEffect: ConsumableEffect | null = isSubscription ? null : consumableEffect;
  const isReadingPass = effectiveEffect === 'READING_PASS_CARD';
  // §2.5 / §2 谓词：不发币效果（阅读卡 / 书籍买断 / VIP 畅读订阅）coin / bonus 与 bonus_expire_days 恒 0
  // （UI 锁死，不让人填了再被 server 拒）
  const lockedZeroAmounts = isZeroEntitlementEffect(effectiveEffect) || isVipReading;
  // A8 §3 / #773：配对任一行已发布（first_published_at 非空）→ 锁时长。新建不受 gate。
  const durationBlockingChannels = isEdit
    ? (pairedChannels ?? []).filter((c) => c.firstPublishedAt != null).map((c) => c.paymentMethod)
    : [];
  const durationLocked = isReadingPass && durationBlockingChannels.length > 0;
  const originalDurationSeconds = item ? readingPassDurationSecondsOf(item) : null;
  const durationChanged =
    isReadingPass && (Number(durationSeconds || '0') || null) !== originalDurationSeconds;
  // 越界要当场说明白：只是把保存按钮置灰的话，运营既拿不到服务端 422、也看不出是这一项挡住了
  const durationOverflow =
    isReadingPass &&
    durationSeconds !== '' &&
    Number(durationSeconds) > MAX_READING_PASS_DURATION_SECONDS;

  const buildTitleI18n = (): TitleI18n =>
    Object.fromEntries(
      Object.entries(title)
        .map(([k, v]) => [k, v.trim()] as const)
        .filter(([, v]) => v.length > 0),
    ) as TitleI18n;

  // STRIPE 权益写门禁（#465）：coin 省略（服务端复制基线）；bonus 留空 = 省略（新建由服务端预填派生下限，编辑保持
  // 已存值），填值须 ≥ 下限（服务端权威裁决）。CONSUMABLE 发币与周期发币订阅同一规则（70 §2）。
  // 返回要提交的权益子集（键存在 = 显式传，缺 = 省略）。
  function stripeEntitlementPatch(): { bonus_amount?: string } {
    // 不发币效果不套加赠下限（coin / bonus 恒 0）→ 省略，由服务端按效果派生 0，不从被锁死的 UI 状态回灌
    if (lockedZeroAmounts) return {};
    return bonusAmount.trim() !== '' ? { bonus_amount: bonusAmount } : {};
  }

  /**
   * IAP 行提交的 coin / bonus + 有效期：不发币效果一律 `'0'` / `0`（§2.5 / §2 谓词）。
   * 取锁定值而非 UI 状态——切换效果后旧输入仍留在 state 里，直接回灌会撞服务端 shape 校验。
   */
  const submitCoinAmount = lockedZeroAmounts ? '0' : coinAmount || '0';
  const submitBonusAmount = lockedZeroAmounts ? '0' : bonusAmount || '0';
  // BONUS 有效期恒永久（走查 #619 ③）：不发币效果与发币效果同值 `0`，后台无编辑入口
  const submitBonusExpireDays = 0;

  /** 创建只交 duration_seconds；编辑也只交 duration（服务端合并已存 tier_id）*/
  function readingPassParams(): { duration_seconds: number } | null {
    if (!isReadingPass) return null;
    return { duration_seconds: Number(durationSeconds || '0') };
  }

  const saveMutation = useMutation({
    mutationFn: async (): Promise<void> => {
      const title_i18n = buildTitleI18n();
      if (isEdit && item?.id) {
        const params = readingPassParams();
        const body: UpdateProductMetadataRequest = {
          name: name.trim(),
          // access_level_id 不在 Update 契约（A8 §3）：编辑态只读展示、不提交
          title_i18n,
          bonus_expire_days: submitBonusExpireDays,
          // consumable_effect 不在 Update 契约（A8 §3 `.strict()` 拒传）——本弹窗编辑态也不让改，故不提交。
          // consumable_effect_params **只在时长真的变了才提交**：服务端收到本字段即走逻辑产品级原子更新
          // （锁 IAP/STRIPE 配对行同写），没改就提交等于无谓触发一次双行写 + 一次 gate 判定。
          ...(params && durationChanged ? { consumable_effect_params: params } : {}),
          // 只有 STRIPE 行提交本字段：IAP 行传非 null 会被服务端 422（Update 体不带 payment_method，
          // 服务端按已存通道判），IAP 行连 null 也不必传，避免无谓写。
          ...(isStripe ? { stripe_price_id: stripePriceId.trim() || null } : {}),
          // 订阅效果不在 Update 契约（创建后不可换）—— 编辑态只读展示，不提交
          ...(isSubscription ? { subscription_period: subscriptionPeriod || null } : {}),
          ...(isIapPaired
            ? // STRIPE / 框架通道：coin 锁定基线（省略=保持），bonus 只可 ≥ 派生下限
              stripeEntitlementPatch()
            : // IAP：照常录入 coin/bonus（无门禁）；不发币效果被锁成 '0'
              {
                coin_amount: submitCoinAmount,
                bonus_amount: submitBonusAmount,
              }),
        };
        await productMetadataApi.update(item.id, body);
        return;
      }
      const params = readingPassParams();
      const common = {
        app_id: appId,
        vendor_product_id: vendorProductId.trim(),
        access_level_id: accessLevelId.trim() || null,
        payment_method: paymentMethod,
        product_type: productType,
        name: name.trim(),
        title_i18n,
        bonus_expire_days: submitBonusExpireDays,
        // 效果只挂 CONSUMABLE（A8 §2：SUBSCRIPTION 传非 null 即 422）；params 仅 READING_PASS_CARD 非空，
        // COIN_GRANT 省略 → 服务端落 `{}`（§2.5：COIN_GRANT 的 params 恒空对象，任何 key 均非法）。
        // 订阅显式提交效果（全新录入 = 周期发币，补通道 = 同逻辑产品既有效果）：撞到效果不同的软删历史版本时
        // 得到明确的冲突，而不是被静默沿用
        ...(isSubscription
          ? {
              subscription_effect: subscriptionEffect ?? 'COIN_PERIOD_GRANT',
              subscription_period: subscriptionPeriod || null,
            }
          : { consumable_effect: consumableEffect }),
        ...(params ? { consumable_effect_params: params } : {}),
        // IAP 行不带该键（Zod superRefine 对 IAP 的非 null 值直接 422）
        ...(isStripe ? { stripe_price_id: stripePriceId.trim() || null } : {}),
      };
      const body: CreateProductMetadataRequest = isIapPaired
        ? // STRIPE / 框架通道创建：coin 省略（服务端复制 IAP 基线）；bonus 留空=派生下限，填值须 ≥；STRIPE 订阅周期 WEEKLY / MONTHLY
          {
            ...common,
            ...stripeEntitlementPatch(),
          }
        : // IAP 创建：照常录入（不发币效果被锁成 '0'）
          {
            ...common,
            coin_amount: submitCoinAmount,
            bonus_amount: submitBonusAmount,
          };
      await productMetadataApi.create(body);
    },
    onSuccess: async () => {
      setSaveError(null);
      const display = (title.zh || title.en || vendorProductId).trim();
      toast(`已${isEdit ? '保存' : '录入'}产品「${display}」业务效果`, {
        tone: 'pos',
        icon: 'IconCheck',
      });
      await queryClient.invalidateQueries({ queryKey: ['product-metadata', appId] });
      onClose();
    },
    onError: (err) => {
      // 字段 / 冲突定位在弹窗内就近展示（设计稿 B2「保存错误」），toast 只做轻提示
      setSaveError(describeProductMetadataSaveError(err, channelName));
      toast(err instanceof Error ? err.message : '保存失败', { tone: 'neg', icon: 'IconAlert' });
    },
  });

  // 校验：写权限 + 非空白 en + 非空白 name + 新建需非空白 vendor_product_id + 新建各通道需非空 access_level（A8 §2）
  // + STRIPE 编辑态仍需非空 access_level（#465）+ 非 pending
  const canSubmit =
    canWrite &&
    (title.en ?? '').trim().length > 0 &&
    name.trim().length > 0 &&
    (isEdit || vendorProductId.trim().length > 0) &&
    // A8 §2：各结算通道行创建（含复活）统一必填 —— 关联固定、PATCH 不收，留空的行只能删除重录；
    // 编辑态沿用既有判据（IAP 配对通道行仍须非空）
    ((isEdit && !isIapPaired) || accessLevelId.trim().length > 0) &&
    // 原生 STRIPE 订阅周期须为 WEEKLY / MONTHLY（70 §2），未选或带入不合法周期时先在本地拦住
    (!periodLimitedToWeeklyMonthly ||
      !isSubscription ||
      isStripeSubscriptionPeriod(subscriptionPeriod)) &&
    // 阅读卡：产品位必选、时长为正整数（§2.5 effect_wellformed），且 access_level_id 必填
    // （服务端「READING_PASS_CARD 行必须携带 access_level_id」）；时长被 gate 锁住时只是不可改、不阻断其他字段保存
    (!isReadingPass ||
      (Number(durationSeconds || '0') > 0 &&
        !durationOverflow &&
        accessLevelId.trim().length > 0 &&
        !(durationLocked && durationChanged))) &&
    // stripe_price_id 可留空（未配 = 不可发起 direct Checkout，是合法中间态），但填了就必须是 canonical 格式，
    // 否则服务端 Zod / DB CHECK 都会拒——本地先拦住，省一次往返。
    (!isStripe ||
      stripePriceId.trim() === '' ||
      /^price_[A-Za-z0-9]{1,249}$/.test(stripePriceId.trim())) &&
    // 注意：`stripeVendorIdMalformed` **不在**本判据里 —— 契约规定 canonical `prod_…` 不在写入侧拦截
    // （见 STRIPE_VENDOR_PRODUCT_ID_PATTERN 注释）。拦下去会让「已成交但 vendor id 非 canonical」的
    // 真实事故行建不出来，反而堵死补救路径；这一项只出可见告警。
    !saveMutation.isPending;

  return (
    <Dialog
      width={640}
      title={
        isEdit
          ? `编辑业务效果 · ${item?.title}`
          : isAddChannel
            ? `补通道 · ${channelName} · ${prefillTitle ?? prefillAccessLevel}`
            : '录入产品业务效果'
      }
      subtitle="vendor_product_id / 通道 / 类型 / 权益效果为身份字段（创建后不可改）· Stripe / PayerMax / Airwallex 权益按 IAP 基线 + App 比例派生受控，bonus_ratio 为派生只读"
      icon="IconCoin"
      onClose={onClose}
      footer={
        <>
          <div style={{ flex: 1 }} />
          <button className="btn" onClick={onClose}>
            取消
          </button>
          <button
            className="btn btn-accent"
            disabled={!canSubmit}
            title={!canWrite ? FORBIDDEN_ROLE_TITLE : undefined}
            onClick={() => saveMutation.mutate()}
          >
            保存
          </button>
        </>
      }
    >
      <div style={{ padding: '4px 18px 8px' }}>
        {/* 身份字段（Adapty source of truth）：新建可录、编辑只读 */}
        <FormRow
          label="vendor_product_id"
          required
          hint={
            isStripe
              ? 'Stripe Dashboard 上的 Product id（prod_…）· 创建后不可改'
              : isFramework
                ? 'Adapty 自定义 store 的 product ID · 不要求 prod_ 前缀 · 创建后不可改'
                : 'Adapty Dashboard 上的 product id · 创建后不可改'
          }
        >
          {isEdit ? (
            <>
              <span className="mono" style={{ fontSize: 12.5 }}>
                {item?.vendorProductId}
              </span>
              {/* 编辑态该字段只读（身份字段）→ 只能告警，改不了：写全格式判据 + 准确后果 + 重建前置 */}
              {/*
                用 `chip-warn` 形态而非 `color: var(--warn)`：`styles/tokens.css:301-304` 的
                `.chip-warn` 刻意**不**拿 `--warn`（`oklch(0.74 0.15 75)`）当文字色，改用更深的
                `oklch(0.45 0.12 75)` 配 `--warn-soft` 底 —— 设计系统自己承认 L=0.74 不适合当正文，
                11px 琥珀压近白底对比度不足。复用该 class 同时拿到深色与浅色主题两套底色。
              */}
              {stripeVendorIdMalformed && (
                <div
                  className="chip-warn"
                  style={{
                    display: 'block',
                    fontSize: 11,
                    lineHeight: 1.6,
                    borderRadius: 6,
                    padding: '6px 8px',
                    marginTop: 6,
                    maxWidth: 420,
                  }}
                >
                  非 canonical Stripe Product id · 该行无法发起 direct Checkout。
                  {STRIPE_VENDOR_PRODUCT_ID_FORMAT_HINT}
                  {STRIPE_VENDOR_PRODUCT_ID_IMPACT_HINT}
                  {STRIPE_VENDOR_PRODUCT_ID_REBUILD_HINT}
                </div>
              )}
            </>
          ) : (
            <>
              <input
                className="input mono"
                value={vendorProductId}
                autoFocus
                placeholder={
                  isStripe
                    ? 'prod_SxYz1234'
                    : isFramework
                      ? 'pmx.coins_500'
                      : 'com.example.app.coins_500'
                }
                /*
                  **通道无关地取 255**（= shared Zod `vendor_product_id.max(255)`，对所有通道同值）。
                  曾按 canonical 收窄成 254（`prod_` + 249）——那是**写入侧拦截**，方向与契约相反
                  （§1 / §2.2：不在写入侧拦截、不在 serving 侧隐藏，改由告警承接）。且 HTML
                  `maxLength` 是**静默**截断，没有被拦下的保存按钮那种反馈：运营粘贴一个 255 字符的
                  既有非 canonical id 来补建已成交商品时，会被无声截掉最后一个字符 —— 而
                  vendor_product_id 是 C 端 webhook 的 exact-match 反查键，错一个字符与完全没配等价
                  （不命中 → DLQ + 不发放，用户已扣款）。canonical 的长度约束交给下方告警文案。
                */
                maxLength={255}
                list="adapty-product-candidates"
                onChange={(e) => setVendorProductId(e.target.value.replace(/\s/g, ''))}
              />
              <datalist id="adapty-product-candidates">
                {unconfiguredCandidates.map((p) => (
                  <option
                    key={p.vendor_product_id}
                    value={p.vendor_product_id}
                    /*
                      入口与出口要一致：候选源是 C 端真实成交记录（商店 SKU 居多），STRIPE 模式下直接选
                      `com.…` 会得到一条发不起 direct Checkout 的行 —— 同一屏「推荐这个值」与
                      「这个值会导致支付失败」互相否定。故 STRIPE 态给不合规候选加 `label` 标注。
                      ⚠️ `label` 在 datalist 里如何呈现由浏览器决定，本项未在真浏览器逐个验证，故
                      **不对任何具体浏览器的行为下断言**。风险方向是 `label` **顶替** value 显示：
                      那样所有不合规候选都渲染成同一串警告文字、vendor id 本体不可见，运营反而分辨
                      不出该选哪条 —— 而补建已成交商品正是这条入口的用途。为此把 vendor id 本体
                      写进 `label` 前缀：无论浏览器取 value 还是取 label，值都看得见。
                      它仍只是**尽力而为的提示**；真正的告警面是选中后输入框下方那条（不依赖
                      datalist 渲染）。不过滤掉这些候选。
                    */
                    label={
                      isStripe && !isCanonicalStripeVendorProductId(p.vendor_product_id)
                        ? `${p.vendor_product_id} · ⚠ 非 prod_ 格式，发不起 Stripe Checkout`
                        : undefined
                    }
                  />
                ))}
              </datalist>
              {unconfiguredCandidates.length > 0 && (
                <div style={{ fontSize: 11, color: 'var(--fg-muted)', marginTop: 5 }}>
                  <span className="mono">{unconfiguredCandidates.length}</span> 个 C
                  端已购买但未配置的 vendor_product_id 可下拉选择；新品请手输（从{' '}
                  {isStripe ? 'Stripe' : 'Adapty'} Dashboard 抄）
                  {isStripe && '。候选多为商店 SKU，选中非 prod_ 格式的值会发不起 direct Checkout'}
                </div>
              )}
              {/*
                只告警、不拦保存（契约不在写入侧拦截）。既然放行，就更得把三件事说全：
                完整格式判据（前缀 + 字符集 + 长度）· 准确后果（不改可售判定，但可售 + 已接入 Checkout 时必炸）
                · 身份字段落库后只能删行重建，且删除本身高危。
              */}
              {/*
                用 `chip-warn` 形态而非 `color: var(--warn)`：`styles/tokens.css:301-304` 的
                `.chip-warn` 刻意**不**拿 `--warn`（`oklch(0.74 0.15 75)`）当文字色，改用更深的
                `oklch(0.45 0.12 75)` 配 `--warn-soft` 底 —— 设计系统自己承认 L=0.74 不适合当正文，
                11px 琥珀压近白底对比度不足。复用该 class 同时拿到深色与浅色主题两套底色。
              */}
              {stripeVendorIdMalformed && (
                <div
                  className="chip-warn"
                  style={{
                    display: 'block',
                    fontSize: 11,
                    lineHeight: 1.6,
                    borderRadius: 6,
                    padding: '6px 8px',
                    marginTop: 6,
                    maxWidth: 420,
                  }}
                >
                  {STRIPE_VENDOR_PRODUCT_ID_FORMAT_HINT}
                  {STRIPE_VENDOR_PRODUCT_ID_IMPACT_HINT}
                  {STRIPE_VENDOR_PRODUCT_ID_REBUILD_HINT}
                  仍可保存（补建已成交商品时需要），但请先确认这就是 Stripe 上的真实 Product id。
                </div>
              )}
            </>
          )}
        </FormRow>

        {/* access_level_id（product 级，跨渠道客户端匹配键）：新建各通道必填（A8 §2，须已建 §8.3 关联）；
            编辑态只读 —— 产品级关联创建后固定，PATCH 传入即 VALIDATION_FAILED（A8 §3） */}
        <FormRow
          label="access_level_id"
          required={!isEdit}
          hint={
            isEdit
              ? '产品级关联 · 创建后不可改（需要另一个 access level 请创建新产品）'
              : isReadingPass
                ? '阅读卡必填 · 与服务端生成的 tier_id 在同 App 内一一对应（缺失即拒）'
                : isIapPaired
                  ? `${isStripe ? 'STRIPE' : channelName} 必填 · 据此唯一命中同 App active IAP 基线派生权益`
                  : 'Adapty Dashboard 该 product 的 access level 标识（跨渠道匹配键）· 必填，须已在「Adapty 产品关联」建立关联，创建后不可改'
          }
        >
          {isEdit ? (
            <span className="mono" style={{ fontSize: 12.5 }}>
              {item?.accessLevelId ?? '—'}
            </span>
          ) : (
            <>
              <input
                className="input mono"
                value={accessLevelId}
                onChange={(e) => setAccessLevelId(e.target.value)}
                placeholder="如 premium / coins_500（必填）"
                style={{ maxWidth: 360 }}
              />
              {isAddChannel && accessLevelId === prefillAccessLevel && (
                <div style={{ fontSize: 11, color: 'var(--fg-muted)', marginTop: 5 }}>
                  已从组内既有行预填
                </div>
              )}
            </>
          )}
        </FormRow>

        {/* stripe_price_id（A8 §9.6）：仅 STRIPE 行可填；缺失 = 不可发起 direct Checkout（可见告警，但不改可售） */}
        {isStripe && (
          <FormRow
            label="stripe_price_id"
            hint="Stripe Dashboard 上该产品的 Price id（price_…）· 仅用于发起 direct Checkout 的路由，不承载金额 / 币种 / 周期"
          >
            <input
              className="input mono"
              value={stripePriceId}
              onChange={(e) => setStripePriceId(e.target.value.replace(/\s/g, ''))}
              placeholder="price_1234567890"
              style={{ maxWidth: 360 }}
            />
            {stripePriceId.trim() === '' && (
              <div
                className="chip chip-warn"
                style={{ fontSize: 10, marginTop: 6, display: 'inline-block' }}
              >
                未配置 · 该行无法发起 Stripe direct Checkout
              </div>
            )}
            {stripePriceId.trim() !== '' &&
              !/^price_[A-Za-z0-9]{1,249}$/.test(stripePriceId.trim()) && (
                <div style={{ fontSize: 11, color: 'var(--neg)', marginTop: 6 }}>
                  格式须为 <span className="mono">price_</span> 开头的 Stripe Price id
                </div>
              )}
          </FormRow>
        )}

        {/* 通道（PK 组件，per-通道）：新建可选、编辑只读 */}
        <FormRow
          label="支付通道"
          required
          hint={
            isAddChannel
              ? '每 product 每通道各一行 · 创建后不可改 · 组内已有的通道不可再选'
              : '每 product 每通道各一行 · 创建后不可改'
          }
        >
          {isEdit ? (
            <span className="chip" style={{ fontSize: 10 }}>
              {paymentMethodLabel(paymentMethod)}
            </span>
          ) : (
            <Select
              value={paymentMethod}
              onChange={(e) => {
                const next = e.target.value as SettlementChannel;
                setPaymentMethod(next);
                setSaveError(null);
                // 框架通道只售一次性商品：产品类型锁 CONSUMABLE（订阅不可选）
                if (isFrameworkSettlementChannel(next)) setProductType('CONSUMABLE');
                // 切到 STRIPE 且订阅周期不在 WEEKLY / MONTHLY → 归一为 WEEKLY（季 / 年只留给 IAP）
                if (
                  next === 'STRIPE' &&
                  isSubscription &&
                  !isStripeSubscriptionPeriod(subscriptionPeriod)
                ) {
                  setSubscriptionPeriod('WEEKLY');
                }
              }}
              style={{ maxWidth: 240 }}
            >
              {PAYMENT_METHOD_OPTIONS.map((o) => (
                <option
                  key={o.value}
                  value={o.value}
                  // 补通道：组内已有该通道行 → 不可再建；订阅商品不可补框架通道
                  disabled={
                    (existingChannels ?? []).includes(o.value) ||
                    (isAddChannel && isSubscription && isFrameworkSettlementChannel(o.value))
                  }
                >
                  {o.label}
                </option>
              ))}
            </Select>
          )}
        </FormRow>

        <FormRow label="产品类型" required>
          {isEdit ? (
            <span className="chip" style={{ fontSize: 10 }}>
              {productType === 'SUBSCRIPTION' ? '订阅' : '充值'}
            </span>
          ) : isFramework ? (
            // 框架通道只售一次性商品（A8 §2 校验③）：锁定 CONSUMABLE，订阅不可选
            <div className="hstack" style={{ gap: 8, flexWrap: 'wrap' }}>
              <span className="chip" style={{ fontSize: 10.5 }}>
                <Icons.IconLock size={10} />
                充值（CONSUMABLE）
              </span>
              <span style={{ fontSize: 11.5, color: 'var(--fg-muted)' }}>
                {channelName} 只售一次性商品，订阅不可选
              </span>
            </div>
          ) : (
            <Select
              value={productType}
              onChange={(e) => setProductType(e.target.value as ProductType)}
              style={{ maxWidth: 240 }}
            >
              {PRODUCT_TYPE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Select>
          )}
        </FormRow>

        {/* ---- 权益效果（#400 / dd614）：仅 CONSUMABLE；SUBSCRIPTION 传非 null 即 422（A8 §2）---- */}
        {!isSubscription && (
          <FormRow
            label="权益效果"
            required
            hint={
              isEdit
                ? '创建后不可改（与 product_type 同档，A8 §3 拒传）· 需换效果请建新产品'
                : '一次性购买成交后按本值单点分派履约（契约 03-数据字典/70 §2.5 闭集）'
            }
          >
            {/* 不可改字段的 hover 说明挂在包裹层：Select 不透传 title（原生属性不在其 props 面上）*/}
            <span
              style={{ display: 'inline-block' }}
              title={isEdit ? 'consumable_effect 创建后不可改（服务端 .strict() 拒传）' : undefined}
            >
              <Select
                value={consumableEffect}
                disabled={isEdit}
                onChange={(e) => {
                  const next = e.target.value as ConsumableEffect;
                  setConsumableEffect(next);
                  // 切到不发币效果：coin / bonus 恒 0（§2.5），先把 UI 状态归零，避免锁死后仍残留旧输入。
                  // BONUS 有效期不在此列——它已恒 `0` 且无受控 state（#619 ③）。
                  if (isZeroEntitlementEffect(next)) {
                    setCoinAmount('0');
                    setBonusAmount('0');
                  }
                }}
                style={{ maxWidth: 280 }}
              >
                {EFFECT_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Select>
            </span>
            {isEdit && item?.consumableEffect === null && (
              <div className="chip chip-neg" style={{ fontSize: 10, marginTop: 6 }}>
                该行未登记效果 · C 端履约无从分派、不进 serving
              </div>
            )}
          </FormRow>
        )}

        {/* 逐效果参数子表单：READING_PASS_CARD = tier_id（产品位身份）+ duration_seconds（权益时长）*/}
        {isReadingPass && (
          <>
            {isEdit && (
              <FormRow
                label="产品位 tier_id"
                hint="创建后由服务端写入，不可改 · 新卡 UUID v7，存量 CARD_A / CARD_B 仍合法"
              >
                <span className="mono" style={{ fontSize: 13 }}>
                  {tierId || '—'}
                </span>
              </FormRow>
            )}

            <FormRow
              label="权益时长"
              required
              hint={`该产品位当前提供的阅读时长（秒，正整数，上限 ${MAX_READING_PASS_DURATION_SECONDS}）· 调整时长不改 tier_id、不改与 access_level 的关联`}
              hintFull
            >
              <div className="hstack" style={{ gap: 8, flexWrap: 'wrap', rowGap: 6 }}>
                <DigitInput
                  value={durationSeconds}
                  onChange={setDurationSeconds}
                  width={140}
                  disabled={durationLocked}
                  placeholder="3600"
                />
                <span style={{ fontSize: 11.5, color: 'var(--fg-muted)' }}>
                  秒 ={' '}
                  <span className="mono" style={{ color: 'var(--fg)' }}>
                    {formatDurationSeconds(Number(durationSeconds || '0') || null)}
                  </span>
                </span>
                {DURATION_PRESETS.map((p) => (
                  <button
                    key={p.seconds}
                    type="button"
                    className="btn btn-ghost btn-sm"
                    disabled={durationLocked}
                    onClick={() => setDurationSeconds(String(p.seconds))}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
              {/*
                越界值本地就把保存按钮置灰了 —— 那就必须在字段旁说清为什么，否则运营既拿不到服务端
                422、按钮又不响应，只能看着一个没有理由的灰按钮猜。
              */}
              {durationOverflow && (
                <div style={{ fontSize: 11, color: 'var(--neg)', marginTop: 6 }}>
                  超出上限 <span className="mono">{MAX_READING_PASS_DURATION_SECONDS}</span>{' '}
                  秒（库存列 <span className="mono">reading_pass_cards.duration_seconds</span> 的
                  int4 列宽）—— 超界的时长在配置侧看着合法，一旦成交就在履约的 INSERT
                  上炸，而那时钱已经收了。
                </div>
              )}
              {durationLocked && (
                <div
                  className="card-flat"
                  style={{
                    marginTop: 8,
                    padding: '8px 10px',
                    background: 'var(--warn-soft)',
                    fontSize: 11.5,
                    lineHeight: 1.6,
                  }}
                >
                  <b>时长已锁 · 该卡位已发布过</b>
                  <div style={{ color: 'var(--fg-muted)', marginTop: 2 }}>
                    配对任一行写过 <span className="mono">first_published_at</span> 后不可改时长
                    （已发布通道：
                    <b>
                      {durationBlockingChannels
                        .map((m) => channelStoreLabel(m, platform))
                        .join(' / ')}
                    </b>
                    ）。换时长请走原子换代，不要先下架再改。
                  </div>
                </div>
              )}
              {!durationLocked && isEdit && (
                <div style={{ marginTop: 8, fontSize: 11.5, color: 'var(--fg-muted)' }}>
                  保存时效果参数按<b>支付商品级原子更新</b>写入 —— 服务端在同一事务锁 IAP 行与
                  配对行同写，无需逐行再改一次。
                </div>
              )}
            </FormRow>
          </>
        )}

        {/* 运营识别名（product 级，跨通道行应同值）*/}
        <FormRow
          label="识别名"
          required
          hint="运营内部识别 / 排障用 · 客户端不显示 · 跨通道行应同值"
        >
          <input
            className="input"
            value={name}
            maxLength={100}
            placeholder="如：500币-主力档"
            onChange={(e) => setName(e.target.value)}
          />
        </FormRow>

        {/* 订阅效果（70 §2）：只读 —— 全新录入恒周期发币，补通道沿用同逻辑产品，编辑态展示已存值（创建后不可换）*/}
        {isSubscription && (
          <FormRow
            label="订阅效果"
            hint={
              isVipReading
                ? 'VIP 畅读不发币（COIN / BONUS 恒 0）· 原生 App 尚未开放履约，不进 serving · 创建后不可改'
                : '每计费周期（首购 / 续订 / RESUBSCRIBE）一次性发放该周期 COIN + BONUS · 创建后不可改'
            }
          >
            <span className="chip chip-info" style={{ fontSize: 10 }}>
              {subscriptionEffectLabel(subscriptionEffect)}（{subscriptionEffect}）
            </span>
          </FormRow>
        )}

        {isSubscription && (
          <FormRow
            label="订阅周期"
            hint={
              isStripe
                ? 'STRIPE 订阅仅支持 WEEKLY / MONTHLY · 须与 IAP 基线同周期'
                : isVipReading
                  ? 'VIP 畅读仅 WEEKLY / MONTHLY · 须与 Adapty 实际计费周期一致 · STRIPE 配对行须同周期'
                  : '须与 Adapty 实际计费周期一致（C 端据此派生 billing_period）· COIN / BONUS 为每期发放量 · STRIPE 配对行须同周期（STRIPE 仅 WEEKLY / MONTHLY）'
            }
          >
            <Select
              value={subscriptionPeriod}
              onChange={(e) => setSubscriptionPeriod(e.target.value as SubscriptionPeriod | '')}
              style={{ maxWidth: 200 }}
            >
              <option value="">（未配置）</option>
              {periodOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Select>
          </FormRow>
        )}

        <FormRow
          label="产品标题"
          required
          hint="订单历史 / 客服查询展示 · 必含英文（C 端确定性 fallback）"
          hintFull
        >
          <MultiLangFormRows
            label="标题"
            value={title}
            onChange={setTitle}
            requiredLangs={['en']}
            availableLangs={enabledLangs}
            placeholderMap={{ en: 'Product title (English)' }}
            placeholderTpl={(zh) => `标题 (${zh})`}
          />
        </FormRow>

        {/*
          不发币效果权益金额锁死（§2.5 / §2「coin / bonus 恒 '0'、bonus_expire_days 恒 0」）：
          服务端已拒非 0，UI 这里不渲染 input，只读展示 0 —— 不让运营填了再吃 VALIDATION_FAILED。
        */}
        {lockedZeroAmounts ? (
          <FormRow
            label="权益金额"
            hint={
              isVipReading
                ? 'VIP 畅读不发币：COIN / BONUS 与 BONUS 有效期恒 0（权益是阅读权限，不是钱包额度）'
                : isReadingPass
                  ? '小时阅读卡不发币：COIN / BONUS 与 BONUS 有效期恒 0（权益由上面的产品位 + 时长承载）'
                  : '书籍买断不发币：COIN / BONUS 与 BONUS 有效期恒 0（买断哪一本由购买链路的 novel_id 决定，产品不承载价格档）'
            }
            hintFull
          >
            <div className="hstack" style={{ gap: 10, flexWrap: 'wrap', rowGap: 6 }}>
              {[
                { label: 'COIN', value: '0' },
                { label: 'BONUS', value: '0' },
                { label: 'BONUS 有效期', value: '永久' },
              ].map((f) => (
                <span key={f.label} className="chip" style={{ fontSize: 10 }}>
                  {f.label}
                  <span className="mono" style={{ marginLeft: 4, color: 'var(--fg-muted)' }}>
                    {f.value}
                  </span>
                </span>
              ))}
            </div>
          </FormRow>
        ) : (
          <>
            {/* COIN：IAP 可录；STRIPE 锁定 IAP 基线（提交省略 → 服务端复制），只读展示 */}
            <FormRow
              label="COIN"
              hint={
                isIapPaired
                  ? '锁定 IAP 基线（提交时复制，不可改）'
                  : isSubscription
                    ? '每计费周期一次性发放的 coin 计数（整数，可售须 > 0）'
                    : '购买发放的 coin 计数（整数）'
              }
            >
              {isIapPaired ? (
                <span className="mono" style={{ fontSize: 12.5, color: 'var(--fg-muted)' }}>
                  {isEdit
                    ? item?.coinAmount
                    : prefillEffectFrom?.paymentMethod === 'IAP'
                      ? `${prefillEffectFrom.coinAmount}（IAP 基线）`
                      : '由 IAP 基线派生'}
                </span>
              ) : (
                <DigitInput value={coinAmount} onChange={setCoinAmount} />
              )}
            </FormRow>

            {/*
              赠送组（BONUS / 赠送比例 / 有效期）：充值每笔一次、周期发币订阅每计费周期一次 —— 同一规则（70 §2：
              IAP 人工维护、STRIPE ≥ IAP bonus + ceil(IAP coin × ratio)，与订阅周期长度无关）
            */}
            <FormRow
              label={createBonusFloor !== null ? `BONUS（≥ ${createBonusFloor}）` : 'BONUS'}
              hint={
                isIapPaired
                  ? isEdit
                    ? minBonus !== null
                      ? `派生下限 ${minBonus} · 只可 ≥ 下限 · 留空=保持当前值`
                      : '只可 ≥ 派生下限 · 留空=保持当前值'
                    : createBonusFloor !== null
                      ? `派生下限 ${createBonusFloor}（取组内既有配对行）· 只可 ≥ 下限 · 留空=系统按下限预填`
                      : '留空=系统按 IAP 基线 + App 比例派生下限，可上调'
                  : isSubscription
                    ? '每计费周期随 COIN 一次性发放的 bonus 计数（整数）'
                    : '附赠 bonus 计数（整数）'
              }
            >
              <div className="hstack" style={{ gap: 8, flexWrap: 'wrap', rowGap: 6 }}>
                <DigitInput
                  value={bonusAmount}
                  onChange={setBonusAmount}
                  // 编辑态留空 = 不提交（保持已存值），占位显示已存值；新建留空 = 服务端预填派生下限
                  placeholder={isIapPaired ? (isEdit ? item?.bonusAmount : '派生') : undefined}
                />
                {/*
                  编辑态 STRIPE 行一键套用当前派生下限：PATCH 不带 bonus 即保持已存值，低于新下限的行（如 IAP 基线
                  上调后）只有运营显式提交 ≥ 下限的值才能再次可售
                */}
                {isEdit && isIapPaired && minBonus !== null && (
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => setBonusAmount(minBonus)}
                  >
                    套用派生下限
                  </button>
                )}
              </div>
            </FormRow>
            {/* 赠送比例：#465 响应派生只读（不再手输）*/}
            <FormRow
              label="赠送比例"
              hint="由 coin/bonus 派生（bonus÷coin）· 只读展示 · 客户端渲染「+X%」"
            >
              <span className="mono" style={{ fontSize: 12.5, color: 'var(--fg-muted)' }}>
                {isEdit ? formatBonusRatio(item?.bonusRatio ?? '0') : '系统派生'}
              </span>
            </FormRow>
            {/*
              BONUS 有效期恒永久（走查 #619 ③）：后台不开「限期赠送」入口，提交恒 `0`。
              只读展示而非删行 —— 运营需要看见「这笔 bonus 不会过期」这条事实。
            */}
            <FormRow label="BONUS 有效期" hint="恒永久不过期 · 后台不提供限期赠送配置">
              <span className="mono" style={{ fontSize: 12.5, color: 'var(--fg-muted)' }}>
                永久
              </span>
            </FormRow>
          </>
        )}

        {/* 框架通道：价格不在本弹窗（A8 §10.3 单独配置），保存后为未上架、未定价（设计稿 B2）*/}
        {isFramework && !isEdit && (
          <div style={{ padding: '14px 0 8px' }}>
            <div
              className="card-flat"
              style={{
                padding: '10px 12px',
                fontSize: 11.5,
                lineHeight: 1.6,
                color: 'var(--fg-muted)',
              }}
            >
              <div style={{ color: 'var(--fg)', fontWeight: 500, marginBottom: 2 }}>
                价格不在这里设置
              </div>
              保存后该行为未上架、未定价。由 root / admin 在列表中点「设置价格」录入 {channelName}{' '}
              成交价，没有价格的行在 C 端不可售。本弹窗不出现 stripe_price_id。
            </div>
          </div>
        )}

        {saveError && (
          <div
            role="alert"
            style={{
              margin: '8px 0',
              padding: '10px 12px',
              background: 'var(--neg-soft)',
              borderRadius: 'var(--radius)',
              fontSize: 11.5,
              lineHeight: 1.55,
            }}
          >
            <div style={{ fontWeight: 500 }}>{saveError.title}</div>
            {saveError.hint && <div style={{ color: 'var(--fg-muted)' }}>{saveError.hint}</div>}
            <div
              className="mono"
              style={{ fontSize: 10.5, color: 'var(--fg-muted)', marginTop: 2 }}
            >
              {saveError.code}
            </div>
          </div>
        )}
      </div>
    </Dialog>
  );
}
